/**
 * Routing.
 *
 * Unauthenticated users see only the sign-in screen. There is no "protected
 * route" wrapper doing a redirect after the page has already mounted and fired
 * its queries — the whole authenticated tree simply does not exist without a
 * session, so no component can request data it has no token for.
 */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Shell } from './components/Shell';
import { Spinner } from './components/ui';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { InsightsPage } from './pages/Insights';
import { FrameworksPage } from './pages/Frameworks';
import { AssessmentsPage } from './pages/Assessments';
import { AssessmentDetailPage } from './pages/AssessmentDetail';
import { GapsPage } from './pages/Gaps';
import { RiskRegisterPage } from './pages/RiskRegister';
import { PlansPage } from './pages/Plans';
import { PoliciesPage } from './pages/Policies';
import { VendorsPage } from './pages/Vendors';
import { OrganizationsPage } from './pages/Organizations';
import { UsersPage } from './pages/Users';
import { PolicyEditorPage } from './pages/PolicyEditor';
import { CustomTemplatesPage } from './pages/CustomTemplates';
import { ReportsPage } from './pages/Reports';
import { AuditTrailPage } from './pages/AuditTrail';
import { SecurityPage } from './pages/Security';
import { ScansPage } from './pages/Scans';
import { CreditsPage } from './pages/Credits';
import { ControlCataloguePage, ControlCoveragePage } from './pages/ControlCatalogue';
import { NewsPage } from './pages/News';
import { ManualPage } from './pages/Manual';
import { TranslationsPage } from './pages/Translations';
import { ContentReviewPage } from './pages/ContentReview';
import { StatementOfApplicabilityPage } from './pages/StatementOfApplicability';
import { GettingStartedPage } from './pages/GettingStarted';
import { IncidentsPage } from './pages/Incidents';
import { IncidentDetailPage } from './pages/IncidentDetail';
import { CraConformityPage } from './pages/CraConformity';
import { CraProductDetailPage } from './pages/CraProductDetail';
import { CalendarPage } from './pages/Calendar';
import { TrustCentrePage } from './pages/TrustCentre';
import { TrustCentreSettingsPage } from './pages/TrustCentreSettings';
import { TrustPortalPage } from './pages/TrustPortal';
import { VendorAssessmentsPage } from './pages/VendorAssessments';
import { PublicAssessmentPage } from './pages/PublicAssessment';

export function App() {
  const { session, loading } = useAuth();
  const location = useLocation();

  // The vendor questionnaire is opened from an e-mail by a supplier who has no
  // account here, so it MUST be answered before any session handling below.
  // Falling through would either hold them on the refresh spinner or, worse,
  // show them a sign-in screen they can never get past — their credential is
  // the token in the URL, not a password.
  // `/assessment/` (singular, trailing slash) cannot collide with the internal
  // `/assessments` and `/assessments/:id` pages.
  // Same reasoning as the vendor questionnaire below: a prospect opening a
  // customer's trust page has no account here and never will. '/trust/' cannot
  // collide with the authenticated '/trust-centre' settings screen.
  if (location.pathname.startsWith('/trust/')) {
    return (
      <Routes>
        {/* The external portal is reachable WITHOUT a tenant login, exactly like
            the public trust page. Its own PORTAL token (held in memory by the
            page) is the only credential, and it never touches a tenant session.
            Listed before the catch-all slug route so the more specific path wins. */}
        <Route path="/trust/:slug/portal" element={<TrustPortalPage />} />
        <Route path="/trust/:slug" element={<TrustCentrePage />} />
      </Routes>
    );
  }

  if (location.pathname.startsWith('/assessment/')) {
    return (
      <Routes>
        <Route path="/assessment/:token" element={<PublicAssessmentPage />} />
      </Routes>
    );
  }

  // Held until the silent refresh resolves, so we never flash the sign-in
  // screen at someone who is in fact still signed in.
  if (loading) {
    return (
      <div className="min-h-dvh grid place-items-center bg-surface-sunken">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (!session) return <LoginPage />;

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/frameworks" element={<FrameworksPage />} />
        <Route path="/catalogue" element={<ControlCataloguePage />} />
        <Route path="/catalogue/:key" element={<ControlCoveragePage />} />
        <Route path="/assessments" element={<AssessmentsPage />} />
        <Route path="/assessments/:id" element={<AssessmentDetailPage />} />
        <Route path="/gaps" element={<GapsPage />} />
        <Route path="/risk-register" element={<RiskRegisterPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/getting-started" element={<GettingStartedPage />} />
        <Route path="/incidents" element={<IncidentsPage />} />
        <Route path="/incidents/:id" element={<IncidentDetailPage />} />
        <Route path="/cra-conformity" element={<CraConformityPage />} />
        <Route path="/cra-conformity/:id" element={<CraProductDetailPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        <Route path="/policies/templates" element={<CustomTemplatesPage />} />
        <Route path="/policies/:id" element={<PolicyEditorPage />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/vendors/assessments" element={<VendorAssessmentsPage />} />
        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/audit" element={<AuditTrailPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/manual" element={<ManualPage />} />
        <Route path="/manual/:slug" element={<ManualPage />} />
        <Route path="/translations" element={<TranslationsPage />} />
        <Route path="/content-watch" element={<ContentReviewPage />} />
        <Route path="/soa" element={<StatementOfApplicabilityPage />} />
        <Route path="/trust-centre" element={<TrustCentreSettingsPage />} />
        <Route path="/scans" element={<ScansPage />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
